import React, { useState, useEffect, useRef } from 'react';
import { 
  Store, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Plus,
  TrendingUp,
  Package,
  Users,
  Star,
  Filter,
  Search,
  RefreshCw,
  AlertCircle,
  Settings,
  ShoppingBag,
  MessageSquare,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  Zap
} from 'lucide-react';
import useMarketplaceStore from '../../store/marketplaceStore';
import useAuthStore from '../../store/authStore';
import marketplaceAPI from '../../services/marketplace.api';
import { toast } from 'react-hot-toast';
import ListBusinessModal from './ListBusinessModal';
import EditBusinessModal from './EditBusinessModal';
import InventoryAlert from './InventoryAlert';
import MessagesPanel from './MessagesPanel';
import { useNavigate } from 'react-router-dom';

const BusinessManagementTab = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showListModal, setShowListModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileStats, setShowMobileStats] = useState(false);
  
  // Message states
  const [showMessages, setShowMessages] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  const { 
    userStats,
    getUserStats,
    userBusiness,
    businessExpiry,
    setUserBusiness,
    updateBusinessExpiry,
    clearUserBusiness,
    isLoading
  } = useMarketplaceStore();

  useEffect(() => {
    loadStats();
    loadUserBusiness();
  }, []);

  // Timer effect for business expiry
  useEffect(() => {
    if (userBusiness?.expiresAt && !userBusiness.isExpired) {
      const timer = setInterval(() => {
        const expiry = new Date(userBusiness.expiresAt);
        const now = new Date();
        const diff = expiry - now;
        
        if (diff <= 0) {
          // Business has expired
          setUserBusiness({ ...userBusiness, isExpired: true });
          clearInterval(timer);
        }
      }, 60000);
      
      return () => clearInterval(timer);
    }
  }, [userBusiness?.expiresAt, userBusiness?.isExpired]);

  const loadStats = async () => {
    try {
      await getUserStats();
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadUserBusiness = async () => {
    try {
      const response = await marketplaceAPI.getUserBusiness();
      setUserBusiness(response.data);
    } catch (error) {
      console.error('Failed to load user business:', error);
    }
  };

  const refreshStats = async () => {
    await getUserStats();
    await loadUserBusiness();
  };

  const handleRenewNow = () => {
    navigate('/data', { 
      state: { 
        autoSelectAmount: 200,
        message: 'Purchase data to renew your business for another 24 hours!'
      } 
    });
  };

  // Message functions
  const loadConversations = async () => {
    try {
      const response = await marketplaceAPI.getConversations();
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load messages');
    }
  };

  const loadMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const response = await marketplaceAPI.getMessages(conversationId);
      const transformedMessages = response.data.messages.map(msg => ({
        id: msg.id,
        content: msg.content,
        createdAt: msg.createdAt,
        read: msg.read,
        sender: {
          id: msg.sender.id,
          firstName: msg.sender.firstName,
          lastName: msg.sender.lastName,
          avatar: msg.sender.avatar
        }
      }));
      setMessages(transformedMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (receiverId, content) => {
    if (!selectedConversation) return;

    try {
      await marketplaceAPI.sendMessage({
        receiverId: receiverId,
        content: content,
        businessId: selectedConversation.business?.id
      });
      
      await loadMessages(selectedConversation.id);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleBusinessCreated = () => {
    loadStats();
    loadUserBusiness();
    toast.success('Business created successfully!');
  };

  const handleBusinessUpdated = () => {
    loadStats();
    loadUserBusiness();
    setShowEditModal(false);
    setSelectedBusiness(null);
    toast.success('Business updated successfully!');
  };

  const handleStatusChange = async (businessId, newStatus) => {
    try {
      await marketplaceAPI.updateBusinessStatus(businessId, newStatus);
      toast.success(`Business ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'}`);
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to update status');
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business? This action cannot be undone.')) {
      return;
    }

    try {
      await marketplaceAPI.deleteBusiness(businessId);
      toast.success('Business deleted successfully');
      loadStats();
      loadUserBusiness();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to delete business');
    }
  };

  const handleEditBusiness = (business) => {
    setSelectedBusiness(business);
    setShowEditModal(true);
  };

  const handleViewBusiness = (businessId) => {
    navigate(`/community/marketplace/business/${businessId}`);
  };

  const handleManageProducts = (businessId) => {
    navigate(`/marketplace/business/${businessId}/products/manage`);
  };

  const handleManageServices = (businessId) => {
    navigate(`/marketplace/business/${businessId}/services/manage`);
  };

  const handleManageOrders = (businessId) => {
    navigate(`/marketplace/business/${businessId}/orders`);
  };

  const handleAddProduct = (businessId) => {
    navigate(`/marketplace/business/${businessId}/products/manage`, {
      state: { openAddModal: true }
    });
  };

  const handleAddService = (businessId) => {
    navigate(`/marketplace/business/${businessId}/services/manage`, {
      state: { openAddModal: true }
    });
  };

  const handleViewProduct = (productId) => {
    const business = userStats?.businesses?.list.find(
      b => b.products?.some(p => p.id === productId)
    );
    if (business) {
      navigate(`/marketplace/business/${business.id}/products/manage`);
    } else {
      navigate('/marketplace/products/manage');
    }
  };

  // Helper function to calculate time left
  const getTimeLeft = () => {
    if (!userBusiness?.expiresAt || userBusiness?.isExpired) return null;
    const expiry = new Date(userBusiness.expiresAt);
    const now = new Date();
    const diff = expiry - now;
    
    if (diff <= 0) return { expired: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, minutes, expired: false };
  };

  const showRenewalBanner = userBusiness?.isExpired === true;
  const timeLeft = getTimeLeft();

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'bg-green-50 text-green-700 border-green-200',
      INACTIVE: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      DRAFT: 'bg-gray-50 text-gray-700 border-gray-200',
      SUSPENDED: 'bg-red-50 text-red-700 border-red-200',
      PENDING: 'bg-orange-50 text-orange-700 border-orange-200',
      DELETED: 'bg-red-50 text-red-700 border-red-200'
    };
    return colors[status] || colors.DRAFT;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'ACTIVE': return Eye;
      case 'INACTIVE': return EyeOff;
      case 'PENDING': return Clock;
      case 'SUSPENDED': return XCircle;
      case 'DELETED': return XCircle;
      default: return Clock;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const businesses = userStats?.businesses?.list || [];

  const filteredBusinesses = businesses.filter(business => {
    if (statusFilter !== 'all' && business.status !== statusFilter) return false;
    if (searchQuery && !business.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const unreadCount = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  // Stats cards for mobile
  const statsCards = [
    { label: 'Businesses', value: userStats?.businesses?.count || 0, sub: `${userStats?.businesses?.active || 0} active`, icon: Store, color: 'from-primary-500 to-primary-600' },
    { label: 'Products', value: userStats?.businesses?.totalProducts || 0, sub: 'Total', icon: Package, color: 'from-green-500 to-green-600' },
    { label: 'Services', value: userStats?.businesses?.totalServices || 0, sub: 'Total', icon: TrendingUp, color: 'from-blue-500 to-blue-600' },
    { label: 'Orders', value: userStats?.orders?.total || 0, sub: `${userStats?.orders?.pending || 0} pending`, icon: ShoppingBag, color: 'from-orange-500 to-orange-600' },
    { label: 'Rating', value: userStats?.reviews?.averageRating?.toFixed(1) || '0.0', sub: `${userStats?.reviews?.total || 0} reviews`, icon: Star, color: 'from-yellow-500 to-yellow-600' },
  ];

  return (
    <div className="space-y-3 sm:space-y-5">
      {/* Header */}
      <div className="bg-white rounded-xl p-3 sm:p-5 shadow-neomorph border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex space-x-2 sm:space-x-3">
            <button
              onClick={() => setShowListModal(true)}
              className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-2.5 sm:px-4 py-1.5 sm:py-2 shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs sm:text-sm font-medium flex items-center"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Add New</span> Business
            </button>
          </div>
          
          {/* Mobile: Toggle Stats Button */}
          <button
            onClick={() => setShowMobileStats(!showMobileStats)}
            className="sm:hidden flex items-center justify-between w-full px-3 py-2 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset"
          >
            <span className="text-xs font-medium text-gray-600">View Stats</span>
            <ChevronRight className={`h-3.5 w-3.5 text-gray-500 transition-transform ${showMobileStats ? 'rotate-90' : ''}`} />
          </button>
        </div>
      </div>

      {/* Renewal Banner */}
      {/*showRenewalBanner && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4 shadow-neomorph border border-orange-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Business Listing Expired!</h4>
                <p className="text-sm text-gray-600">Your business listing has expired. Purchase VTU to renew it for another 24 hours.</p>
              </div>
            </div>
            <button
              onClick={handleRenewNow}
              className="px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white font-medium rounded-lg shadow-md shadow-orange-500/30 hover:shadow-lg transition-all flex items-center justify-center"
            >
              <Zap className="h-4 w-4 mr-2" />
              Renew Now (₦200+)
            </button>
          </div>
        </div>
      )*/}

      {/* Expiry Timer Display */}
      {/*userBusiness && !userBusiness.isExpired && timeLeft && !timeLeft.expired && (
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 shadow-neomorph border border-blue-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Business expires in:</span>
                <span className="ml-2 text-lg font-bold text-blue-700">
                  {timeLeft.hours}h {timeLeft.minutes}m
                </span>
                {userBusiness.hoursLeft && (
                  <p className="text-xs text-gray-500 mt-0.5">{userBusiness.hoursLeft} hours remaining</p>
                )}
              </div>
            </div>
            <button
              onClick={handleRenewNow}
              className="px-3 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-200 transition-all flex items-center justify-center"
            >
              <Zap className="h-3.5 w-3.5 mr-1.5" />
              Renew Early
            </button>
          </div>
          <div className="mt-3 w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${userBusiness.percentageLeft || 100}%` }}
            />
          </div>
        </div>
      )*/}

      {/* Stats Overview - Mobile toggleable, Desktop always visible */}
      <div className={`${showMobileStats ? 'block' : 'hidden'} sm:block`}>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white rounded-lg p-2.5 sm:p-4 shadow-neomorph border border-gray-100">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">{stat.label}</p>
                    <p className="text-sm sm:text-base md:text-lg font-bold text-gray-800">{stat.value}</p>
                    <p className="text-[9px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">{stat.sub}</p>
                  </div>
                  <div className={`p-1.5 sm:p-2 bg-gradient-to-br ${stat.color} rounded-md shadow-md flex-shrink-0 ml-2`}>
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Inventory Alerts Section */}
      <InventoryAlert 
        onViewProduct={handleViewProduct}
        onRefresh={refreshStats}
      />

      {/* Messages Panel */}
      {showMessages && (
        <div className="bg-white rounded-xl shadow-neomorph border border-gray-100 overflow-hidden h-[500px] sm:h-[600px]">
          {selectedConversation ? (
            <div className="relative h-full">
              <button
                onClick={() => setSelectedConversation(null)}
                className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10 bg-white rounded-lg p-1.5 sm:p-2 shadow-neomorph hover:shadow-neomorph-hover transition-all"
              >
                <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
              </button>
              <MessagesPanel
                selectedUser={{
                  id: selectedConversation.otherUser.id,
                  firstName: selectedConversation.otherUser.firstName,
                  lastName: selectedConversation.otherUser.lastName,
                  avatar: selectedConversation.otherUser.avatar,
                  businessTags: selectedConversation.otherUser.businessTags || []
                }}
                onBack={() => setSelectedConversation(null)}
                externalMessages={messages}
                externalSendMessage={handleSendMessage}
                externalIsLoading={loadingMessages}
                businessContext={selectedConversation.business}
              />
            </div>
          ) : (
            <div className="flex flex-col sm:grid sm:grid-cols-3 h-full">
              {/* Conversations List */}
              <div className="border-b sm:border-b-0 sm:border-r border-gray-200 overflow-y-auto sm:col-span-1">
                <div className="p-3 sm:p-4 border-b border-gray-200 bg-[#E8ECF5]">
                  <h3 className="text-xs sm:text-sm font-semibold text-gray-800 flex items-center">
                    <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-primary-600" />
                    Messages
                  </h3>
                </div>
                
                {conversations.length === 0 ? (
                  <div className="p-6 sm:p-8 text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3">
                      <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500">No messages yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => {
                          setSelectedConversation(conv);
                          loadMessages(conv.id);
                        }}
                        className={`w-full p-3 sm:p-4 text-left hover:bg-[#E8ECF5] transition-colors ${
                          selectedConversation?.id === conv.id ? 'bg-primary-50' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <div className="relative flex-shrink-0">
                            {conv.otherUser.avatar ? (
                              <img
                                src={conv.otherUser.avatar}
                                alt=""
                                className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl object-cover shadow-neomorph-inset"
                              />
                            ) : (
                              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex items-center justify-center">
                                <span className="text-white font-semibold text-xs sm:text-sm">
                                  {conv.otherUser.firstName[0]}
                                </span>
                              </div>
                            )}
                            {conv.unreadCount > 0 && (
                              <span className="absolute -top-1 -right-1 h-4 w-4 sm:h-5 sm:w-5 bg-gradient-to-br from-red-500 to-red-600 rounded-full shadow-md shadow-red-500/30 text-[9px] sm:text-xs text-white flex items-center justify-center">
                                {conv.unreadCount}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center justify-between gap-1 mb-0.5 sm:mb-1">
                              <h4 className="text-xs sm:text-sm font-semibold text-gray-800 truncate">
                                {conv.otherUser.firstName} {conv.otherUser.lastName}
                              </h4>
                              <span className="text-[9px] sm:text-xs text-gray-500 bg-[#E8ECF5] px-1.5 sm:px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                                {new Date(conv.lastMessageAt).toLocaleDateString()}
                              </span>
                            </div>
                            
                            {conv.business && (
                              <p className="text-[9px] sm:text-xs text-primary-600 mb-0.5 truncate bg-primary-50 px-1.5 sm:px-2 py-0.5 rounded-lg shadow-neomorph-inset inline-block">
                                Re: {conv.business.name}
                              </p>
                            )}
                            
                            {conv.lastMessage && (
                              <p className="text-[9px] sm:text-xs text-gray-500 truncate mt-0.5 sm:mt-1">
                                {conv.lastMessage.content}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Empty state for right panel - hidden on mobile */}
              <div className="hidden sm:flex sm:col-span-2 items-center justify-center bg-[#E8ECF5]">
                <div className="text-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <MessageSquare className="h-7 w-7 sm:h-8 sm:w-8 text-primary-600" />
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500">Select a conversation to start messaging</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search and Filter */}
      {!showMessages && (
        <div className="bg-white rounded-xl p-3 sm:p-4 shadow-neomorph border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-2 flex-1">
              <Search className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search your businesses..."
                className="flex-1 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              />
            </div>
            
            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-2 sm:px-3 py-1.5 sm:py-2 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                >
                  <option value="all">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="ACTIVE">Active</option>
                  <option value="DRAFT">Draft</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="SUSPENDED">Suspended</option>
                </select>
              </div>
              
              <button
                onClick={refreshStats}
                disabled={isLoading}
                className="bg-white rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs sm:text-sm font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <RefreshCw className={`h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                <span className="hidden xs:inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Businesses List - Mobile Card View, Desktop Table View */}
      {!showMessages && (
        <div className="bg-white rounded-xl p-3 sm:p-5 shadow-neomorph border border-gray-100">
          {/* Mobile: Card View */}
          <div className="sm:hidden space-y-3">
            {filteredBusinesses.length > 0 ? (
              filteredBusinesses.map((business) => {
                const StatusIcon = getStatusIcon(business.status);
                return (
                  <div key={business.id} className="bg-white rounded-xl shadow-neomorph border border-gray-200 overflow-hidden">
                    <div className={`h-1 w-full ${
                      business.status === 'ACTIVE' ? 'bg-green-500' : 
                      business.status === 'PENDING' ? 'bg-orange-500' : 'bg-gray-400'
                    }`} />
                    <div className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <div className="h-10 w-10 flex-shrink-0">
                            {business.logoUrl ? (
                              <img src={business.logoUrl} alt={business.name} className="h-10 w-10 rounded-lg object-cover shadow-neomorph-inset" />
                            ) : (
                              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex items-center justify-center">
                                <Store className="h-5 w-5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-sm font-semibold text-gray-800 truncate">{business.name}</h4>
                            <p className="text-xs text-gray-500">{business.city}, {business.state}</p>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-lg text-[9px] font-medium border ${getStatusColor(business.status)} shadow-neomorph-inset`}>
                          <StatusIcon className="h-2.5 w-2.5 mr-0.5" />
                          {business.status}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        <button onClick={() => handleManageProducts(business.id)} className="flex items-center bg-[#E8ECF5] px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                          <Package className="h-3 w-3 text-primary-600 mr-0.5" />
                          <span className="text-[10px] font-medium">{business._count?.products || 0}</span>
                        </button>
                        <button onClick={() => handleManageServices(business.id)} className="flex items-center bg-[#E8ECF5] px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                          <TrendingUp className="h-3 w-3 text-blue-600 mr-0.5" />
                          <span className="text-[10px] font-medium">{business._count?.services || 0}</span>
                        </button>
                        <button onClick={() => handleManageOrders(business.id)} className="flex items-center bg-[#E8ECF5] px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                          <ShoppingBag className="h-3 w-3 text-orange-600 mr-0.5" />
                          <span className="text-[10px] font-medium">{business._count?.orders || 0}</span>
                        </button>
                        <div className="flex items-center bg-[#E8ECF5] px-1.5 py-0.5 rounded-lg shadow-neomorph-inset">
                          <Star className="h-2.5 w-2.5 text-yellow-500 mr-0.5 fill-current" />
                          <span className="text-[10px] font-medium">{business.rating?.toFixed(1) || '0.0'}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-1.5 pt-1 border-t border-gray-100">
                        <button onClick={() => handleViewBusiness(business.id)} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-primary-600">
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleEditBusiness(business)} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-blue-600">
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleManageOrders(business.id)} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-green-600">
                          <ShoppingBag className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleStatusChange(business.id, business.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} disabled={business.status === 'PENDING'} className={`p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover ${business.status === 'PENDING' ? 'opacity-50 cursor-not-allowed' : business.status === 'ACTIVE' ? 'text-yellow-600' : 'text-green-600'}`}>
                          {business.status === 'ACTIVE' ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                        <button onClick={() => handleDeleteBusiness(business.id)} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Store className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">No businesses found</h3>
                <p className="text-xs text-gray-500 mb-3">
                  {searchQuery || statusFilter !== 'all' ? 'Try changing your search or filter' : 'You haven\'t listed any businesses yet'}
                </p>
                <button onClick={() => setShowListModal(true)} className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center">
                  <Plus className="h-4 w-4 mr-2" />
                  List Your Business
                </button>
              </div>
            )}
          </div>

          {/* Desktop: Table View */}
          <div className="hidden sm:block overflow-x-auto">
            {filteredBusinesses.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#E8ECF5]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Business</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Stats</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredBusinesses.map((business) => {
                    const StatusIcon = getStatusIcon(business.status);
                    return (
                      <tr key={business.id} className="hover:bg-[#E8ECF5] transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              {business.logoUrl ? (
                                <img src={business.logoUrl} alt={business.name} className="h-10 w-10 rounded-lg object-cover shadow-neomorph-inset" />
                              ) : (
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex items-center justify-center">
                                  <Store className="h-5 w-5 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-semibold text-gray-800">{business.name}</div>
                              <div className="text-xs text-gray-500">{business.city}, {business.state}</div>
                              {business.pendingOrders > 0 && (
                                <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 shadow-neomorph-inset">
                                  <ShoppingBag className="h-3 w-3 mr-1" />
                                  {business.pendingOrders} pending
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-xs text-gray-700 bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset">
                            {business.category}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-3">
                            <button onClick={() => handleManageProducts(business.id)} className="flex items-center bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset">
                              <Package className="h-3.5 w-3.5 text-primary-600 mr-1" />
                              <span className="text-xs">{business._count?.products || 0}</span>
                            </button>
                            <button onClick={() => handleManageServices(business.id)} className="flex items-center bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset">
                              <TrendingUp className="h-3.5 w-3.5 text-blue-600 mr-1" />
                              <span className="text-xs">{business._count?.services || 0}</span>
                            </button>
                            <button onClick={() => handleManageOrders(business.id)} className="flex items-center bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset">
                              <ShoppingBag className="h-3.5 w-3.5 text-orange-600 mr-1" />
                              <span className="text-xs">{business._count?.orders || 0}</span>
                            </button>
                            <div className="flex items-center bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset">
                              <Star className="h-3.5 w-3.5 text-yellow-500 mr-1 fill-current" />
                              <span className="text-xs">{business.rating?.toFixed(1) || '0.0'}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3 mt-2 text-xs">
                            {business._count?.products === 0 && (
                              <button onClick={() => handleAddProduct(business.id)} className="text-primary-600 hover:text-primary-700 bg-primary-50 px-2 py-0.5 rounded-lg shadow-neomorph-inset flex items-center">
                                <Plus className="h-3 w-3 mr-0.5" />
                                Add Product
                              </button>
                            )}
                            {business._count?.services === 0 && (
                              <button onClick={() => handleAddService(business.id)} className="text-primary-600 hover:text-primary-700 bg-primary-50 px-2 py-0.5 rounded-lg shadow-neomorph-inset flex items-center">
                                <Plus className="h-3 w-3 mr-0.5" />
                                Add Service
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${getStatusColor(business.status)} shadow-neomorph-inset`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {business.status}
                          </span>
                          {business.status === 'PENDING' && (
                            <p className="text-xs text-orange-600 mt-1 bg-orange-50 px-2 py-0.5 rounded-lg shadow-neomorph-inset">Awaiting approval</p>
                          )}
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-500">
                          <span className="bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset">
                            {formatDate(business.createdAt)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1.5">
                            <button onClick={() => handleViewBusiness(business.id)} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-primary-600" title="View">
                              <Eye className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleEditBusiness(business)} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-blue-600" title="Edit">
                              <Edit className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleManageOrders(business.id)} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-green-600" title="Orders">
                              <ShoppingBag className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleStatusChange(business.id, business.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} disabled={business.status === 'PENDING'} className={`p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover ${business.status === 'PENDING' ? 'opacity-50 cursor-not-allowed' : business.status === 'ACTIVE' ? 'text-yellow-600' : 'text-green-600'}`} title={business.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}>
                              {business.status === 'ACTIVE' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                            <button onClick={() => handleDeleteBusiness(business.id)} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-red-600" title="Delete">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="flex flex-wrap gap-1.5 mt-2 pt-1 border-t border-gray-100">
                            <button onClick={() => handleManageProducts(business.id)} className="text-xs text-gray-600 hover:text-primary-600 bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset flex items-center">
                              <Package className="h-3 w-3 mr-1" /> Products
                            </button>
                            <button onClick={() => handleManageServices(business.id)} className="text-xs text-gray-600 hover:text-primary-600 bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset flex items-center">
                              <TrendingUp className="h-3 w-3 mr-1" /> Services
                            </button>
                            <button onClick={() => navigate(`/marketplace/business/${business.id}/dashboard`)} className="text-xs text-gray-600 hover:text-primary-600 bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset flex items-center">
                              <Settings className="h-3 w-3 mr-1" /> Dashboard
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Store className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No businesses found</h3>
                <p className="text-sm text-gray-500 mb-6">
                  {searchQuery || statusFilter !== 'all' ? 'Try changing your search or filter' : 'You haven\'t listed any businesses yet'}
                </p>
                <button onClick={() => setShowListModal(true)} className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-6 py-2.5 text-sm font-medium inline-flex items-center">
                  <Plus className="h-4 w-4 mr-2" />
                  List Your First Business
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showListModal && (
        <ListBusinessModal
          onClose={() => setShowListModal(false)}
          onSuccess={handleBusinessCreated}
        />
      )}

      {showEditModal && selectedBusiness && (
        <EditBusinessModal
          business={selectedBusiness}
          onClose={() => {
            setShowEditModal(false);
            setSelectedBusiness(null);
          }}
          onSuccess={handleBusinessUpdated}
        />
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default BusinessManagementTab;